import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  BarChart3, Users, UserCheck, Activity, RefreshCw,
  Calendar, TrendingUp, Clock, History, X, Cpu
} from 'lucide-react';
import config from '../config';
import './StatisticsView.css';
import {
  COLORS, ACTIVITY_LABELS, DASHBOARD_MODEL_LABELS, presencePlusRecente
} from '../constants/activite';
import BadgeMcp from './BadgeMcp';

const API_URL = config.API_URL;


/**
 * Tri des tableaux de statistiques.
 *
 * Une seule mécanique pour les trois tableaux, qui ne partagent pourtant aucune colonne : ce
 * que chacun sait faire, c'est extraire d'une ligne la valeur à comparer. D'où ces tables
 * d'extracteurs, une par tableau, dont les clés nomment les colonnes.
 *
 * Le total des activités sert de valeur de tri pour la colonne qui les affiche : elle porte
 * plusieurs compteurs, et trier sur l'un d'eux arbitrairement surprendrait plus qu'il
 * n'aiderait.
 */
// `null` signale une absence, que le tri renvoie en fin de liste dans les deux sens. Un zéro
// n'en est pas une : zéro connexion est une mesure, et doit se classer comme telle.
const texte = (v) => (v ? String(v).toLocaleLowerCase('fr') : null);
const date = (v) => (v ? new Date(v).getTime() : null);

const TYPES_ACTIVITE = ['DASHBOARD', 'TOOLS', 'AI_ANALYSIS', 'MCP'];
const totalActivites = (activities = {}) => (
  TYPES_ACTIVITE.reduce((somme, cle) => somme + (activities[cle] || 0), 0)
);

const EXTRACTEURS = {
  recent: {
    utilisateur: l => texte(l.name || l.email),
    organisation: l => texte(l.userType === 'member' ? 'organisation' : l.clientCardName),
    type: l => texte(l.userType === 'member' ? (l.role || 'member') : 'client'),
    lastLoginAt: l => date(l.lastLoginAt),
    // Ici les actions sont listées, pas comptées : on trie sur ce que la pastille affiche.
    activites: l => (l.actions || []).filter(a => a.activity !== 'LOGIN').length
  },
  members: {
    utilisateur: m => texte(m.name || m.email),
    // Tous les membres d'un même écran appartiennent à l'organisation sélectionnée : la
    // colonne porte partout la même valeur, il n'y a rien à classer.
    organisation: () => 0,
    role: m => texte(m.role),
    lastLoginAt: m => date(m.lastLoginAt),
    connexions: m => m.totalConnections || 0,
    activites: m => totalActivites(m.activities)
  },
  clients: {
    utilisateur: c => texte(c.name || c.email),
    compte: c => texte(c.clientCardName),
    invitePar: c => texte(c.teamOwnerName),
    lastLoginAt: c => date(c.lastLoginAt),
    connexions: c => c.totalConnections || 0,
    activites: c => totalActivites(c.activities)
  }
};

/**
 * Trie une liste sans la modifier.
 *
 * Les valeurs absentes finissent en bas **dans les deux sens** : une personne qui ne s'est
 * jamais connectée n'a rien à faire en tête d'un classement par date, et l'y voir remonter au
 * premier clic sur « croissant » se lirait comme un défaut de tri.
 */
const trierLignes = (lignes, onglet, { cle, sens }) => {
  const extraire = EXTRACTEURS[onglet]?.[cle];
  if (!extraire) return lignes;

  const facteur = sens === 'asc' ? 1 : -1;
  return [...lignes].sort((a, b) => {
    const va = extraire(a);
    const vb = extraire(b);

    const aVide = va === null || va === undefined;
    const bVide = vb === null || vb === undefined;
    if (aVide && bVide) return 0;
    if (aVide) return 1;
    if (bVide) return -1;

    if (va === vb) return 0;
    return va > vb ? facteur : -facteur;
  });
};

/**
 * En-tête de colonne triable.
 *
 * Défini hors du composant : déclaré à l'intérieur, React le voyait comme un type neuf à chaque
 * rendu et remontait toute la ligne d'en-tête.
 *
 * La flèche est toujours rendue, en retrait tant que la colonne ne trie pas. La réserver au
 * survol ferait sursauter l'en-tête au passage de la souris, les libellés étant en
 * `white-space: nowrap`.
 */
const EnTeteTriable = ({ onglet, cle, children, className, tri, onTrier }) => {
  const actif = tri[onglet].cle === cle;
  return (
    <th
      className={`${className || ''} statistics-th-triable${actif ? ' actif' : ''}`}
      onClick={() => onTrier(onglet, cle)}
      title="Trier sur cette colonne"
    >
      {children}
      <span className="statistics-tri-fleche">{actif ? (tri[onglet].sens === 'asc' ? '▲' : '▼') : '↕'}</span>
    </th>
  );
};

const StatisticsView = ({ token, user, userOrganizations = [] }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [selectedOrgId, setSelectedOrgId] = useState(userOrganizations[0]?.id || '');
  const [period, setPeriod] = useState(30);
  const [activeTab, setActiveTab] = useState('recent'); // 'recent', 'members' ou 'clients'
  const [selectedUserLogins, setSelectedUserLogins] = useState(null); // Pour la modal
  const [chargementActions, setChargementActions] = useState(false);

  /**
   * Tri courant de chaque tableau, mémorisé séparément : passer aux Membres pour y classer par
   * connexions ne doit pas défaire le classement qu'on venait d'établir ailleurs.
   *
   * La dernière connexion, décroissante, est le défaut partout — c'est la question qu'on se
   * pose en ouvrant cet écran : qui est venu récemment.
   */
  const [tri, setTri] = useState({
    recent: { cle: 'lastLoginAt', sens: 'desc' },
    members: { cle: 'lastLoginAt', sens: 'desc' },
    clients: { cle: 'lastLoginAt', sens: 'desc' }
  });

  /** Bascule une colonne : même colonne, on inverse le sens ; nouvelle colonne, on repart de zéro. */
  const changerTri = (onglet, cle) => setTri(actuel => {
    const courant = actuel[onglet];
    const sens = courant.cle === cle && courant.sens === 'desc' ? 'asc' : 'desc';
    return { ...actuel, [onglet]: { cle, sens } };
  });

  /** En-tête de colonne cliquable, lié à l'état de tri de ce composant. */
  const EnTete = useCallback(
    (props) => <EnTeteTriable {...props} tri={tri} onTrier={changerTri} />,
    [tri]
  );

  /**
   * Ouvre le détail d'un utilisateur depuis les onglets Membres et Clients.
   *
   * Ces deux tableaux ne portent que des compteurs : le détail se demande au serveur, qui rend
   * **toutes** les actions de la période. La liste « Dernières connexions », elle, est déjà
   * groupée par utilisateur et ouvre la fenêtre sans requête — mais elle est tronquée à cent
   * entrées toutes personnes confondues, on ne peut donc pas s'en servir ici : on afficherait
   * quelques lignes sous une pastille qui en annonce des centaines.
   */
  const ouvrirDetail = async (personne) => {
    setSelectedUserLogins({ ...personne, actions: [] });
    setChargementActions(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/organizations/${selectedOrgId}/statistics/users/${personne.userId}/actions?days=${period}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.success) {
        setSelectedUserLogins(actuel => (
          actuel && actuel.userId === personne.userId
            ? { ...actuel, actions: data.data.actions, dernierePresence: data.data.dernierePresence }
            : actuel
        ));
      }
    } catch (err) {
      console.error('Erreur chargement du détail des actions:', err);
    } finally {
      setChargementActions(false);
    }
  };

  // Charger les statistiques
  const fetchStatistics = async () => {
    if (!selectedOrgId) return;

    setLoading(true);
    setError('');

    try {
      const response = await axios.get(
        `${API_URL}/api/organizations/${selectedOrgId}/statistics?days=${period}&_t=${Date.now()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        setStatistics(response.data.data);
      } else {
        setError(response.data.error || 'Erreur lors du chargement');
      }
    } catch (err) {
      console.error('Erreur chargement statistiques:', err);
      setError(err.response?.data?.error || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedOrgId) {
      fetchStatistics();
    }
  }, [selectedOrgId, period]);

  // Définir l'org par défaut si pas encore sélectionnée
  useEffect(() => {
    if (!selectedOrgId && userOrganizations.length > 0) {
      setSelectedOrgId(userOrganizations[0].id);
    }
  }, [userOrganizations, selectedOrgId]);

  // Formater la date pour l'affichage
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // Formater la date complète
  const formatFullDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Données pour le graphique journalier
  const dailyChartData = useMemo(() => {
    if (!statistics?.daily) return [];
    return statistics.daily.map(d => ({
      ...d,
      dateLabel: formatDate(d.date),
      total: d.members + d.clients
    }));
  }, [statistics?.daily]);

  // Données pour le pie chart (exclure LOGIN qui n'est pas une activité fonctionnelle)
  const activityChartData = useMemo(() => {
    if (!statistics?.byActivity) return [];
    return statistics.byActivity
      .filter(a => a.count > 0 && a.activity !== 'LOGIN')
      .map(a => ({
        name: ACTIVITY_LABELS[a.activity] || a.activity,
        value: a.count,
        percentage: a.percentage,
        fill: COLORS[a.activity] || '#6b7280'
      }));
  }, [statistics?.byActivity]);

  /**
   * Compteurs d'activité de la période, par utilisateur.
   *
   * `recentLogins` est tronqué à cent entrées **toutes personnes confondues** : compter les
   * actions qu'il contient donnait une pastille bien inférieure au total de la période, et
   * contradictoire avec les panneaux Dashboard, Outils et MCP, qui rendent le vrai total.
   * Les onglets Membres et Clients portent déjà ces totaux — on s'en sert ici.
   */
  const totauxParUtilisateur = useMemo(() => {
    const table = new Map();
    [...(statistics?.members || []), ...(statistics?.clients || [])].forEach(p => {
      table.set(p.userId, p.activities || {});
    });
    return table;
  }, [statistics?.members, statistics?.clients]);

  // Grouper les connexions récentes par utilisateur
  const groupedRecentLogins = useMemo(() => {
    if (!statistics?.recentLogins) return [];

    const userMap = new Map();
    statistics.recentLogins.forEach(login => {
      if (!userMap.has(login.userId)) {
        userMap.set(login.userId, {
          userId: login.userId,
          name: login.name,
          email: login.email,
          picture: login.picture,
          userType: login.userType,
          role: login.role,
          teamOwnerName: login.teamOwnerName,
          clientCardName: login.clientCardName,
          lastLoginAt: login.loginAt,
          actions: []
        });
      }
      userMap.get(login.userId).actions.push({
        id: login.id,
        activity: login.activity,
        loginAt: login.loginAt,
        mcpDetails: login.mcpDetails,
        dashboardModel: login.dashboardModel,
        accountName: login.accountName
      });
    });

    // Trier par dernière connexion (plus récent en premier)
    return Array.from(userMap.values()).sort((a, b) =>
      new Date(b.lastLoginAt) - new Date(a.lastLoginAt)
    );
  }, [statistics?.recentLogins]);

  // Tooltip personnalisé pour le bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const members = payload.find(p => p.dataKey === 'members')?.value || 0;
    const clients = payload.find(p => p.dataKey === 'clients')?.value || 0;
    return (
      <div className="stats-tooltip">
        <p className="stats-tooltip-label">{label}</p>
        <p style={{ color: COLORS.members }}>Membres: {members}</p>
        <p style={{ color: COLORS.clients }}>Clients: {clients}</p>
        <p><strong>Total: {members + clients}</strong></p>
      </div>
    );
  };

  // Tooltip personnalisé pour le pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const data = payload[0].payload;
    return (
      <div className="stats-tooltip">
        <p><strong>{data.name}</strong></p>
        <p>{data.value} connexions ({data.percentage}%)</p>
      </div>
    );
  };

  if (!userOrganizations.length) {
    return (
      <div className="step-content">
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
          <h2 style={{ margin: 0 }}>
            <BarChart3 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Statistiques
          </h2>
        </div>
        <div className="statistics-empty">
          <BarChart3 size={48} />
          <h3>Aucune organisation</h3>
          <p>Vous devez faire partie d'une organisation pour accéder aux statistiques.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-content">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <h2 style={{ margin: 0 }}>
          <BarChart3 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Statistiques
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
          {/* Sélecteur d'organisation */}
          {userOrganizations.length > 1 && (
            <select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              className="statistics-select"
            >
              {userOrganizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          )}

          {/* Sélecteur de période */}
          <select
            value={period}
            onChange={(e) => setPeriod(parseInt(e.target.value))}
            className="statistics-select"
          >
            <option value={7}>7 derniers jours</option>
            <option value={14}>14 derniers jours</option>
            <option value={30}>30 derniers jours</option>
            <option value={90}>90 derniers jours</option>
          </select>

          {/* Bouton refresh */}
          <button
            onClick={fetchStatistics}
            className="statistics-refresh-btn"
            disabled={loading}
          >
            <RefreshCw size={18} className={loading ? 'spinning' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div className="statistics-error">{error}</div>
      )}

      {loading ? (
        <div className="statistics-loading">
          <RefreshCw size={32} className="spinning" />
          <p>Chargement des statistiques...</p>
        </div>
      ) : statistics ? (
        <>
          {/* KPIs */}
          <div className="statistics-kpis">
            <div className="statistics-kpi-card">
              <div className="statistics-kpi-icon" style={{ background: '#FAF9F5' }}>
                <TrendingUp size={24} color="#1a1a1a" />
              </div>
              <div className="statistics-kpi-content">
                <span className="statistics-kpi-value">{statistics.total.connections}</span>
                <span className="statistics-kpi-label">Connexions totales</span>
              </div>
            </div>

            <div className="statistics-kpi-card">
              <div className="statistics-kpi-icon" style={{ background: '#f0fdf4' }}>
                <Users size={24} color="#22c55e" />
              </div>
              <div className="statistics-kpi-content">
                <span className="statistics-kpi-value">{statistics.organization.memberCount}</span>
                <span className="statistics-kpi-label">Membres actifs</span>
              </div>
            </div>

            <div className="statistics-kpi-card">
              <div className="statistics-kpi-icon" style={{ background: '#faf5ff' }}>
                <UserCheck size={24} color="#8b5cf6" />
              </div>
              <div className="statistics-kpi-content">
                <span className="statistics-kpi-value">{statistics.organization.clientCount}</span>
                <span className="statistics-kpi-label">Clients</span>
              </div>
            </div>

            <div className="statistics-kpi-card">
              <div className="statistics-kpi-icon" style={{ background: '#fef3c7' }}>
                <Activity size={24} color="#f59e0b" />
              </div>
              <div className="statistics-kpi-content">
                <span className="statistics-kpi-value">{statistics.total.avgPerDay}</span>
                <span className="statistics-kpi-label">Moyenne / jour</span>
              </div>
            </div>
          </div>

          {/* Graphiques */}
          <div className="statistics-charts">
            {/* Histogramme connexions par jour */}
            <div className="statistics-chart-card statistics-chart-large">
              <h3>
                <Calendar size={18} />
                Connexions par jour
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailyChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="dateLabel" stroke="#6b7280" tick={{ fontSize: 12 }} />
                  <YAxis stroke="#6b7280" tick={{ fontSize: 12 }} />
                  <Tooltip content={<CustomBarTooltip />} />
                  <Legend />
                  <Bar dataKey="members" name="Membres" fill={COLORS.members} stackId="a" />
                  <Bar dataKey="clients" name="Clients" fill={COLORS.clients} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Répartition par activité */}
            <div className="statistics-chart-card statistics-chart-small">
              <h3>
                <Activity size={18} />
                Répartition par activité
              </h3>
              {activityChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={activityChartData}
                      cx="50%"
                      cy="45%"
                      innerRadius={50}
                      outerRadius={80}
                      dataKey="value"
                      label={false}
                    >
                      {activityChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="statistics-no-data">
                  <p>Aucune donnée sur cette période</p>
                </div>
              )}
            </div>
          </div>

          {/* Onglets Dernières connexions / Membres / Clients */}
          <div className="statistics-tabs-section">
            <div className="statistics-tabs">
              <button
                className={`statistics-tab ${activeTab === 'recent' ? 'active' : ''}`}
                onClick={() => setActiveTab('recent')}
              >
                <History size={16} />
                Dernières connexions
              </button>
              <button
                className={`statistics-tab ${activeTab === 'members' ? 'active' : ''}`}
                onClick={() => setActiveTab('members')}
              >
                <Users size={16} />
                Membres ({statistics.members.length})
              </button>
              <button
                className={`statistics-tab ${activeTab === 'clients' ? 'active' : ''}`}
                onClick={() => setActiveTab('clients')}
              >
                <UserCheck size={16} />
                Clients ({statistics.clients.length})
              </button>
            </div>

            {/* Tableau Dernières connexions (groupé par utilisateur) */}
            {activeTab === 'recent' && (
              <div className="statistics-table-container">
                {groupedRecentLogins.length > 0 ? (
                  <table className="statistics-table">
                    <thead>
                      <tr>
                        <EnTete onglet="recent" cle="utilisateur">Utilisateur</EnTete>
                        <EnTete onglet="recent" cle="organisation">Organisation / Compte</EnTete>
                        <EnTete onglet="recent" cle="type">Type</EnTete>
                        <EnTete onglet="recent" cle="lastLoginAt">Dernière connexion</EnTete>
                        <EnTete onglet="recent" cle="activites">Activités</EnTete>
                      </tr>
                    </thead>
                    <tbody>
                      {trierLignes(groupedRecentLogins, 'recent', tri.recent).map(userLogin => (
                        <tr
                          key={userLogin.userId}
                          className="clickable-row"
                          onClick={() => ouvrirDetail(userLogin)}
                        >
                          <td className="member-cell">
                            {/* Le flex vit sur ce conteneur, jamais sur le `td`.
                                Un `td` en `display: flex` quitte le modèle des colonnes : sa largeur se
                                calcule alors sur son propre contenu, et le bloc nom + e-mail, qui porte
                                `min-width: 0`, se réduisait à zéro — les noms avaient disparu. */}
                            <div className="member-cell-contenu">
                              {userLogin.picture ? (
                                <img src={userLogin.picture} alt="" className="member-avatar" />
                              ) : (
                                <div className={`member-avatar-placeholder ${userLogin.userType === 'client' ? 'client' : ''}`}>
                                  {(userLogin.name || userLogin.email || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <div className="member-info">
                                <span className="member-name">{userLogin.name || 'Sans nom'}</span>
                                <span className="member-email">{userLogin.email}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            {userLogin.userType === 'member' ? (
                              <span className="organization-name">
                                {userOrganizations.find(org => org.id === selectedOrgId)?.name || '-'}
                              </span>
                            ) : (
                              <span className="client-card-name">{userLogin.clientCardName || '-'}</span>
                            )}
                          </td>
                          <td>
                            {userLogin.userType === 'member' ? (
                              <span className={`role-badge role-${(userLogin.role || 'member').toLowerCase()}`}>
                                {userLogin.role === 'OWNER' ? 'Propriétaire' : userLogin.role === 'ADMIN' ? 'Admin' : 'Membre'}
                              </span>
                            ) : (
                              <span className="role-badge role-client">Client</span>
                            )}
                          </td>
                          <td>
                            <span className="last-login">
                              <Clock size={14} />
                              {formatFullDate(userLogin.lastLoginAt)}
                            </span>
                          </td>
                          <td>
                            <div className="activity-bars">
                              {(() => {
                                // Totaux de la période, et non décompte des cent dernières
                                // actions — voir `totauxParUtilisateur`. Le repli local ne
                                // sert que si l'utilisateur manque aux deux onglets.
                                const counts = totauxParUtilisateur.get(userLogin.userId)
                                  || userLogin.actions.reduce((acc, action) => {
                                    if (action.activity !== 'LOGIN') {
                                      acc[action.activity] = (acc[action.activity] || 0) + 1;
                                    }
                                    return acc;
                                  }, {});
                                /*
                                  La même liste que les deux autres onglets — `TYPES_ACTIVITE`,
                                  et non un tableau écrit à la main.

                                  Celui-ci omettait `AI_ANALYSIS` : la pastille des analyses
                                  s'affichait dans Membres et dans Clients, jamais ici. Une
                                  liste recopiée trois fois dérive au premier ajout, et c'est
                                  exactement ce qui s'est produit.
                                */
                                return TYPES_ACTIVITE.map(activity => (
                                  counts[activity] > 0 && (
                                    <span
                                      key={activity}
                                      className="activity-badge"
                                      style={{ background: COLORS[activity] }}
                                      title={`${ACTIVITY_LABELS[activity]}: ${counts[activity]}`}
                                    >
                                      {counts[activity]}
                                    </span>
                                  )
                                ));
                              })()}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="statistics-no-data">
                    <History size={32} />
                    <p>Aucune connexion sur cette période</p>
                  </div>
                )}
              </div>
            )}

            {/* Tableau Membres */}
            {activeTab === 'members' && (
              <div className="statistics-table-container">
                {statistics.members.length > 0 ? (
                  <table className="statistics-table">
                    <thead>
                      <tr>
                        <EnTete onglet="members" cle="utilisateur">Membre</EnTete>
                        <EnTete onglet="members" cle="organisation">Organisation</EnTete>
                        <EnTete onglet="members" cle="role">Rôle</EnTete>
                        <EnTete onglet="members" cle="lastLoginAt">Dernière connexion</EnTete>
                        <EnTete onglet="members" cle="connexions" className="numeric">Connexions</EnTete>
                        <EnTete onglet="members" cle="activites">Activités</EnTete>
                      </tr>
                    </thead>
                    <tbody>
                      {trierLignes(statistics.members, 'members', tri.members).map(member => (
                        <tr
                          key={member.userId}
                          className="clickable-row"
                          onClick={() => ouvrirDetail({ ...member, userType: 'member' })}
                        >
                          <td className="member-cell">
                            {/* Le flex vit sur ce conteneur, jamais sur le `td`.
                                Un `td` en `display: flex` quitte le modèle des colonnes : sa largeur se
                                calcule alors sur son propre contenu, et le bloc nom + e-mail, qui porte
                                `min-width: 0`, se réduisait à zéro — les noms avaient disparu. */}
                            <div className="member-cell-contenu">
                              {member.picture ? (
                                <img src={member.picture} alt="" className="member-avatar" />
                              ) : (
                                <div className="member-avatar-placeholder">
                                  {(member.name || member.email || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <div className="member-info">
                                <span className="member-name">{member.name || 'Sans nom'}</span>
                                <span className="member-email">{member.email}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="organization-name">
                              {userOrganizations.find(org => org.id === selectedOrgId)?.name || '-'}
                            </span>
                          </td>
                          <td>
                            <span className={`role-badge role-${member.role.toLowerCase()}`}>
                              {member.role === 'OWNER' ? 'Propriétaire' : member.role === 'ADMIN' ? 'Admin' : 'Membre'}
                            </span>
                          </td>
                          <td>
                            <span className="last-login">
                              <Clock size={14} />
                              {formatFullDate(member.lastLoginAt)}
                            </span>
                          </td>
                          <td className="numeric">
                            <strong>{member.totalConnections}</strong>
                          </td>
                          <td>
                            <div className="activity-bars">
                              {TYPES_ACTIVITE.map(activity => (
                                member.activities[activity] > 0 && (
                                  <span
                                    key={activity}
                                    className="activity-badge"
                                    style={{ background: COLORS[activity] }}
                                    title={`${ACTIVITY_LABELS[activity]}: ${member.activities[activity]}`}
                                  >
                                    {member.activities[activity]}
                                  </span>
                                )
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="statistics-no-data">
                    <Users size={32} />
                    <p>Aucun membre dans l'organisation</p>
                  </div>
                )}
              </div>
            )}

            {/* Tableau Clients */}
            {activeTab === 'clients' && (
              <div className="statistics-table-container">
                {statistics.clients.length > 0 ? (
                  <table className="statistics-table">
                    <thead>
                      <tr>
                        <EnTete onglet="clients" cle="utilisateur">Client</EnTete>
                        <EnTete onglet="clients" cle="compte">Compte client</EnTete>
                        <EnTete onglet="clients" cle="invitePar">Invité par</EnTete>
                        <EnTete onglet="clients" cle="lastLoginAt">Dernière connexion</EnTete>
                        <EnTete onglet="clients" cle="connexions" className="numeric">Connexions</EnTete>
                        <EnTete onglet="clients" cle="activites">Activités</EnTete>
                      </tr>
                    </thead>
                    <tbody>
                      {trierLignes(statistics.clients, 'clients', tri.clients).map(client => (
                        <tr
                          key={client.userId}
                          className="clickable-row"
                          onClick={() => ouvrirDetail({ ...client, userType: 'client' })}
                        >
                          <td className="member-cell">
                            {/* Le flex vit sur ce conteneur, jamais sur le `td`.
                                Un `td` en `display: flex` quitte le modèle des colonnes : sa largeur se
                                calcule alors sur son propre contenu, et le bloc nom + e-mail, qui porte
                                `min-width: 0`, se réduisait à zéro — les noms avaient disparu. */}
                            <div className="member-cell-contenu">
                              {client.picture ? (
                                <img src={client.picture} alt="" className="member-avatar" />
                              ) : (
                                <div className="member-avatar-placeholder client">
                                  {(client.name || client.email || '?')[0].toUpperCase()}
                                </div>
                              )}
                              <div className="member-info">
                                <span className="member-name">{client.name || 'Sans nom'}</span>
                                <span className="member-email">{client.email}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="client-card-name">{client.clientCardName || '-'}</span>
                          </td>
                          <td>
                            <span className="invited-by">{client.teamOwnerName}</span>
                          </td>
                          <td>
                            <span className="last-login">
                              <Clock size={14} />
                              {formatFullDate(client.lastLoginAt)}
                            </span>
                          </td>
                          <td className="numeric">
                            <strong>{client.totalConnections}</strong>
                          </td>
                          <td>
                            <div className="activity-bars">
                              {TYPES_ACTIVITE.map(activity => (
                                client.activities[activity] > 0 && (
                                  <span
                                    key={activity}
                                    className="activity-badge"
                                    style={{ background: COLORS[activity] }}
                                    title={`${ACTIVITY_LABELS[activity]}: ${client.activities[activity]}`}
                                  >
                                    {client.activities[activity]}
                                  </span>
                                )
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="statistics-no-data">
                    <UserCheck size={32} />
                    <p>Aucun client associé à l'organisation</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Tableaux de détails d'activité */}
          <div className="activity-details-section">
            {/* Dashboard */}
            <div className="activity-detail-card">
              <h3 style={{ color: COLORS.DASHBOARD }}>
                <BarChart3 size={18} />
                Dashboard
              </h3>
              {statistics.activityDetails?.DASHBOARD?.length > 0 ? (
                <div className="activity-detail-list">
                  {statistics.activityDetails.DASHBOARD.slice(0, 10).map((item, idx) => (
                    <div key={idx} className="activity-detail-item">
                      <div className="activity-detail-user">
                        {item.picture ? (
                          <img src={item.picture} alt="" className="activity-detail-avatar" />
                        ) : (
                          <div className="activity-detail-avatar-placeholder">
                            {(item.name || item.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className="activity-detail-name">{item.name || item.email}</span>
                      </div>
                      <span className="activity-detail-count" style={{ background: COLORS.DASHBOARD }}>{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="activity-detail-empty">Aucune activité</div>
              )}
            </div>

            {/* Outils */}
            <div className="activity-detail-card">
              <h3 style={{ color: COLORS.TOOLS }}>
                <Activity size={18} />
                Outils
              </h3>
              {statistics.activityDetails?.TOOLS?.length > 0 ? (
                <div className="activity-detail-list">
                  {statistics.activityDetails.TOOLS.slice(0, 10).map((item, idx) => (
                    <div key={idx} className="activity-detail-item">
                      <div className="activity-detail-user">
                        {item.picture ? (
                          <img src={item.picture} alt="" className="activity-detail-avatar" />
                        ) : (
                          <div className="activity-detail-avatar-placeholder">
                            {(item.name || item.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className="activity-detail-name">{item.name || item.email}</span>
                      </div>
                      <span className="activity-detail-count" style={{ background: COLORS.TOOLS }}>{item.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="activity-detail-empty">Aucune activité</div>
              )}
            </div>

            {/* MCP */}
            <div className="activity-detail-card">
              <h3 style={{ color: COLORS.MCP }}>
                <Cpu size={18} />
                MCP
              </h3>
              {statistics.mcp?.byUser?.length > 0 ? (
                <div className="activity-detail-list">
                  {statistics.mcp.byUser.slice(0, 10).map((mcpUser, idx) => (
                    <div key={idx} className="activity-detail-item">
                      <div className="activity-detail-user">
                        {mcpUser.user?.picture ? (
                          <img src={mcpUser.user.picture} alt="" className="activity-detail-avatar" />
                        ) : (
                          <div className="activity-detail-avatar-placeholder">
                            {(mcpUser.user?.name || mcpUser.user?.email || '?')[0].toUpperCase()}
                          </div>
                        )}
                        <span className="activity-detail-name">{mcpUser.user?.name || mcpUser.user?.email}</span>
                      </div>
                      <span className="activity-detail-count" style={{ background: COLORS.MCP }}>{mcpUser.totalRequests}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="activity-detail-empty">Aucune activité</div>
              )}
            </div>
          </div>
        </>
      ) : null}

      {/* Modal détail des actions d'un utilisateur */}
      {selectedUserLogins && ReactDOM.createPortal(
        <div className="statistics-modal-overlay" onClick={() => setSelectedUserLogins(null)}>
          <div className="statistics-modal" onClick={e => e.stopPropagation()}>
            <div className="statistics-modal-header">
              <div className="statistics-modal-user">
                {selectedUserLogins.picture ? (
                  <img src={selectedUserLogins.picture} alt="" className="member-avatar" />
                ) : (
                  <div className={`member-avatar-placeholder ${selectedUserLogins.userType === 'client' ? 'client' : ''}`}>
                    {(selectedUserLogins.name || selectedUserLogins.email || '?')[0].toUpperCase()}
                  </div>
                )}
                <div className="member-info">
                  <span className="member-name">{selectedUserLogins.name || 'Sans nom'}</span>
                  <span className="member-email">{selectedUserLogins.email}</span>
                </div>
              </div>
              <button className="statistics-modal-close" onClick={() => setSelectedUserLogins(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="statistics-modal-content">
              <h4>
                Actions sur la période
                {chargementActions ? '' : ` (${selectedUserLogins.actions.length})`}
              </h4>
              {chargementActions && (
                <div className="statistics-no-data"><p>Chargement…</p></div>
              )}
              {!chargementActions && selectedUserLogins.actions.length === 0 && (
                <div className="statistics-no-data"><p>Aucune action sur la période</p></div>
              )}

              {/*
                La présence, quand elle est plus récente que la dernière action.

                La colonne « Dernière connexion » du tableau derrière la compte, cette liste
                l'écarte : sans cette ligne, l'écran annonçait une venue le 2 octobre au-dessus
                d'un détail qui s'arrêtait au 1er — relevé en base jusqu'à seize jours d'écart.
                Elle n'est pas versée dans la liste pour autant : écrite toutes les cinq minutes,
                elle y noierait les actes qu'on vient précisément lire.
              */}
              {!chargementActions && presencePlusRecente(selectedUserLogins) && (
                <div className="statistics-presence-note">
                  <Clock size={14} />
                  <span>
                    Vu le {new Date(selectedUserLogins.dernierePresence).toLocaleString('fr-FR', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                    {selectedUserLogins.actions.length > 0
                      ? ', sans action enregistrée depuis.'
                      : ', sans aucune action sur la période.'}
                  </span>
                </div>
              )}
              <div className="statistics-actions-list">
                {selectedUserLogins.actions.map(action => (
                  <div key={action.id} className={`statistics-action-item ${action.activity === 'MCP' ? 'mcp-action' : ''}`}>
                    <div className="action-info">
                      <span
                        className="activity-badge"
                        style={{ background: COLORS[action.activity] || '#6b7280' }}
                      >
                        {ACTIVITY_LABELS[action.activity] || action.activity}
                      </span>
                      {(action.activity === 'DASHBOARD' || action.activity === 'TOOLS') && action.dashboardModel && (
                        <span className="detail-badge">
                          {DASHBOARD_MODEL_LABELS[action.dashboardModel] || action.dashboardModel}
                        </span>
                      )}
                      {action.activity === 'MCP' && action.mcpDetails && (
                        <BadgeMcp details={action.mcpDetails} />
                      )}
                      {/* Afficher le nom du compte (pour Dashboard/Tools ou MCP) */}
                      {(action.accountName || action.mcpDetails?.accountName) && (
                        <span className="account-badge">
                          {action.accountName || action.mcpDetails?.accountName}
                        </span>
                      )}
                    </div>
                    <span className="action-date">
                      <Clock size={14} />
                      {formatFullDate(action.loginAt)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default StatisticsView;
